/**
 * ShareDialog — workspace + per-asset share modes (inherit/private/tenant/members).
 * Phase 7 extract from pages/Workspace.jsx.
 *
 * iter-121.22:
 *  • Member picker filters by `target.item.tenant_id` (workspace's tenant,
 *    not caller's). Critical when SA shares a workspace they impersonated
 *    into — without it, SA's unscoped /users response leaks cross-tenant
 *    candidates → backend rejects with "not in this tenant".
 *  • Catch block uses `friendlyApiError()` so plan-limit denials surface
 *    the real reason instead of "Save failed".
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import friendlyApiError from "@/lib/apiErrors";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Lock, Globe2, Users as UsersIcon, AlertCircle, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { ShareModeRow } from "@/components/workspace/AssetGrid";

export default function ShareDialog({ target, onOpenChange, users, currentUser, onSaved }) {
    const isAsset = target && target.type !== "workspace";
    const defaultMode = target?.item?.share_mode || (target?.type === "workspace" && target?.item?.kind === "shared" ? "tenant" : "private");
    const [shareMode, setShareMode] = useState(defaultMode);
    const [members, setMembers] = useState(target?.item?.shared_with || []);
    const [picker, setPicker] = useState("");

    useEffect(() => {
        if (target) {
            setShareMode(target.item.share_mode || (target.type === "workspace" && target.item.kind === "shared" ? "tenant" : "private"));
            setMembers(target.item.shared_with || []);
            setPicker("");
        }
    }, [target]);

    // iter-121.22 — narrow picker to the workspace's tenant. Falls back
    // to `currentUser.tenant_id` for asset-level shares where target.item
    // doesn't carry an explicit tenant_id (asset inherits parent ws).
    // MUST be declared BEFORE the early-return below — React Rules of Hooks.
    const targetTenantId = target?.item?.tenant_id || target?.workspace?.tenant_id || currentUser?.tenant_id;
    const addable = useMemo(() => (users || []).filter((u) =>
        u.id !== currentUser?.id &&
        (!targetTenantId || u.tenant_id === targetTenantId) &&
        !members.some((m) => m.user_id === u.id),
    ), [users, currentUser, members, targetTenantId]);

    if (!target) return null;

    const save = async () => {
        try {
            // iter-113 fix: if a teammate was picked but not added (user
            // hit Apply without clicking the + button), implicitly add
            // them so the click feels intentional.
            let pendingMembers = members;
            if (shareMode === "members" && picker && !members.some((m) => m.user_id === picker)) {
                pendingMembers = [...members, { user_id: picker, permission: "view" }];
                setMembers(pendingMembers);
            }
            if (target.type === "workspace") {
                const r = await api.patch(`/workspaces/${target.item.id}/share`, {
                    share_mode: shareMode,
                    shared_with: shareMode === "members" ? pendingMembers : [],
                });
                toast.success("Sharing updated", {
                    description: shareMode === "members"
                        ? `${pendingMembers.length} ${pendingMembers.length === 1 ? "person" : "people"} now have access`
                        : `Mode: ${shareMode}`,
                });
                onSaved(r.data);
            } else {
                const ws = target.workspace;
                await api.patch(`/workspaces/${ws.id}/assets/${target.type}/${target.item.id}/share`, {
                    share_mode: shareMode,
                    shared_with: shareMode === "members" ? pendingMembers : [],
                });
                toast.success(`${target.type} sharing updated`);
                onSaved({});
            }
        } catch (e) {
            toast.error(friendlyApiError(e, "Save failed"));
        }
    };

    const title = isAsset ? `Share ${target.type}` : "Share workspace";
    const subtitle = target.item.name;
    const isPersonal = !isAsset && target.item?.kind === "personal";

    return (
        <Dialog open={!!target} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <div className="text-sm text-muted-foreground font-mono">{subtitle}</div>
                </DialogHeader>

                {isPersonal ? (
                    <div className="p-4 border border-border rounded-sm bg-secondary/40 text-sm flex items-start gap-2" data-testid="share-personal-warning">
                        <AlertCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <div>
                            Personal workspaces can't be shared. Change the workspace kind to <span className="font-mono">Shared</span> first, or create a new shared workspace.
                        </div>
                    </div>
                ) : (
                    <>
                        <div>
                            <label className="eyebrow mb-2 block">Access</label>
                            <div className="space-y-2">
                                {isAsset && (
                                    <ShareModeRow active={shareMode === "inherit"}
                                        onClick={() => setShareMode("inherit")}
                                        icon={AlertCircle} title="Inherit from workspace"
                                        desc="Uses the parent workspace's sharing"
                                        testId="share-mode-inherit" />
                                )}
                                <ShareModeRow active={shareMode === "private"}
                                    onClick={() => setShareMode("private")}
                                    icon={Lock} title="Private"
                                    desc={isAsset ? "Only you can see this asset" : "Only you can access this workspace"}
                                    testId="share-mode-private" />
                                <ShareModeRow active={shareMode === "tenant"}
                                    onClick={() => setShareMode("tenant")}
                                    icon={Globe2} title="Tenant-wide"
                                    desc="Everyone in your organization"
                                    testId="share-mode-tenant" />
                                <ShareModeRow active={shareMode === "members"}
                                    onClick={() => setShareMode("members")}
                                    icon={UsersIcon} title="Specific people"
                                    desc="Only users you invite below"
                                    testId="share-mode-members" />
                            </div>
                        </div>

                        {shareMode === "members" && (
                            <div>
                                <label className="eyebrow mb-1.5 block">Members ({members.length})</label>
                                {members.length > 0 && (
                                    <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto scrollbar-thin">
                                        {members.map((m) => {
                                            const u = users.find((x) => x.id === m.user_id);
                                            return (
                                                <div key={m.user_id} className="flex items-center gap-2 p-2 border border-border rounded-sm bg-secondary/40">
                                                    <div className="w-6 h-6 bg-foreground text-background font-mono text-[9px] font-semibold flex items-center justify-center rounded-sm shrink-0">
                                                        {(u?.name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium truncate">{u?.name}</div>
                                                        <div className="text-[10px] font-mono text-muted-foreground truncate">{u?.email}</div>
                                                    </div>
                                                    <Select value={m.permission} onValueChange={(v) => setMembers(members.map((x) => x.user_id === m.user_id ? { ...x, permission: v } : x))}>
                                                        <SelectTrigger className="h-7 w-[90px] rounded-sm text-xs" data-testid={`share-perm-${m.user_id}`}><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="view">View</SelectItem>
                                                            <SelectItem value="edit">Edit</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <button onClick={() => setMembers(members.filter((x) => x.user_id !== m.user_id))}
                                                            className="text-muted-foreground hover:text-primary p-1"
                                                            data-testid={`share-remove-${m.user_id}`}>
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {addable.length > 0 ? (
                                    <div className="flex gap-2">
                                        <Select value={picker} onValueChange={setPicker}>
                                            <SelectTrigger className="h-9 rounded-sm flex-1 text-sm" data-testid="share-member-picker"><SelectValue placeholder="Add a teammate..." /></SelectTrigger>
                                            <SelectContent>
                                                {addable.map((u) => (
                                                    <SelectItem key={u.id} value={u.id}>{u.name} · <span className="font-mono text-xs text-muted-foreground">{u.email}</span></SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button variant="outline" className="rounded-sm h-9" disabled={!picker}
                                                onClick={() => { setMembers([...members, { user_id: picker, permission: "view" }]); setPicker(""); }}
                                                data-testid="share-member-add">
                                            <Plus className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-xs text-muted-foreground">All teammates already added.</div>
                                )}
                            </div>
                        )}
                    </>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="share-cancel">Cancel</Button>
                    {!isPersonal && (
                        <Button onClick={save} className="rounded-sm" data-testid="share-save">
                            <Check className="h-4 w-4 mr-1.5" /> Apply
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
